import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Wallet, CheckCircle2, XCircle, Clock, ExternalLink, DollarSign,
  Loader2, Hash, Calendar, Users, Phone, Mail, Banknote, FileText,
  Smartphone, PhoneCall, Image, Plus, X, Search,
} from "lucide-react";
import { api } from "../../lib/api";
import { FormPagoReserva } from "../reservar/FormPagoReserva";

interface PagoItem {
  id: number;
  reservaId: number;
  nombrePagador: string;
  cedula?: string;
  telefono?: string;
  email?: string;
  montoUsd: string;
  moneda?: string;
  metodo?: string;
  banco?: string;
  numeroReferencia?: string;
  fechaPago?: string;
  comprobanteUrl?: string;
  notasCliente?: string;
  origen?: string;
  estado: "pendiente" | "verificado" | "rechazado" | "anulado";
  verificadoAt?: string;
  motivoRechazo?: string;
  createdAt: string;
  // Datos de la reserva
  nombreReserva: string;
  fechaReserva: string;
  horaReserva: string;
  numPersonas: number;
  estadoReserva: string;
  sucursalId?: number;
}

interface Resumen {
  total: number;
  pendientes: number;
  verificados: number;
  rechazados: number;
  totalCobradoUsd: string;
  totalPendienteUsd: string;
}

interface ReservaPendiente {
  id: number;
  nombreCliente: string;
  telefono?: string;
  email?: string;
  cif?: string;
  fechaReserva: string;
  horaReserva: string;
  numPersonas: number;
  estado: string;
  zonaNombre: string;
  montoReservaUsd?: number | null;
  sucursalNombre?: string;
}

const ESTADO_LABEL: Record<string, { txt: string; color: string; icono: any }> = {
  pendiente:  { txt: "Pendiente",  color: "bg-amber-100 text-amber-700 border-amber-200",     icono: Clock },
  verificado: { txt: "Aprobado",   color: "bg-emerald-100 text-emerald-700 border-emerald-200", icono: CheckCircle2 },
  rechazado:  { txt: "Rechazado",  color: "bg-rose-100 text-rose-700 border-rose-200",         icono: XCircle },
  anulado:    { txt: "Anulado",    color: "bg-ink-100 text-ink-600 border-ink-200",            icono: XCircle },
};

function fmtUsd(v: string | number | null | undefined) {
  return "$" + Number(v ?? 0).toFixed(2);
}

function fmtFecha(s?: string) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("es-VE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return s; }
}

export function PagosReservasPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<"pendiente" | "verificado" | "rechazado" | "todos">("pendiente");
  const [rechazandoId, setRechazandoId] = useState<number | null>(null);
  const [motivoRechazo, setMotivoRechazo] = useState("");
  const [modalAbierto, setModalAbierto] = useState(false);
  const [reservaSel, setReservaSel] = useState<ReservaPendiente | null>(null);
  const [busqueda, setBusqueda] = useState("");

  const lista = useQuery({
    queryKey: ["pagos-reservas", tab],
    queryFn: () => {
      const q = new URLSearchParams();
      if (tab !== "todos") q.set("estado", tab);
      return api<PagoItem[]>(`/v1/admin/pagos-reservas?${q.toString()}`);
    },
    refetchInterval: 30_000,
  });

  const resumen = useQuery({
    queryKey: ["pagos-reservas", "resumen"],
    queryFn: () => api<Resumen>(`/v1/admin/pagos-reservas/resumen`),
  });

  const modulo = useQuery({
    queryKey: ["pagos-reservas", "modulo"],
    queryFn: () => api<{ activo: boolean }>(`/v1/admin/pagos-reservas/modulo`),
  });

  const toggleModulo = useMutation({
    mutationFn: (activo: boolean) =>
      api(`/v1/admin/pagos-reservas/modulo`, { method: "POST", body: { activo } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pagos-reservas", "modulo"] }),
  });
  const moduloActivo = !!modulo.data?.activo;

  const aprobar = useMutation({
    mutationFn: (id: number) => api(`/v1/admin/pagos-reservas/${id}/aprobar`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pagos-reservas"] }),
  });

  const rechazar = useMutation({
    mutationFn: ({ id, motivo }: { id: number; motivo: string }) =>
      api(`/v1/admin/pagos-reservas/${id}/rechazar`, { method: "POST", body: { motivo } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pagos-reservas"] });
      setRechazandoId(null);
      setMotivoRechazo("");
    },
  });

  const pendientesPago = useQuery({
    queryKey: ["pagos-reservas", "pendientes-pago"],
    queryFn: () => api<ReservaPendiente[]>(`/v1/admin/pagos-reservas/reservas-pendientes-pago`),
    enabled: modalAbierto,
  });

  const reservasFiltradas = (pendientesPago.data ?? []).filter((r) => {
    if (!busqueda.trim()) return true;
    const q = busqueda.toLowerCase();
    return (
      String(r.id).includes(q) ||
      r.nombreCliente.toLowerCase().includes(q) ||
      (r.telefono ?? "").includes(q) ||
      (r.cif ?? "").toLowerCase().includes(q) ||
      (r.zonaNombre ?? "").toLowerCase().includes(q)
    );
  });

  const items = lista.data ?? [];

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-5">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-ink-900 flex items-center gap-3">
            <Wallet className="size-7 text-emerald-500" />
            Pagos de Reservas
          </h1>
          <p className="text-sm text-ink-500 mt-1">
            Verificá los pagos que reportan los clientes. Al aprobar, la reserva se confirma automáticamente.
          </p>
        </div>
        <button
          onClick={() => { setModalAbierto(true); setReservaSel(null); setBusqueda(""); }}
          className="px-4 py-2.5 rounded-xl bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 transition inline-flex items-center gap-2 shadow-sm shrink-0"
        >
          <Plus className="size-4" /> Registrar pago
        </button>
      </header>

      {/* Switch global del módulo */}
      <div className={`card p-4 flex items-center gap-4 border-2 ${
        moduloActivo ? "border-emerald-200 bg-emerald-50/40" : "border-ink-200 bg-ink-50/40"
      }`}>
        <div className="flex-1">
          <div className="font-semibold text-ink-900 flex items-center gap-2">
            {moduloActivo ? "🟢 Módulo activo" : "⚪ Módulo desactivado"}
          </div>
          <div className="text-xs text-ink-500 mt-0.5">
            {moduloActivo
              ? "El formulario de pago aparece en las reservas públicas (sólo para zonas con la opción marcada)."
              : "Aunque tengas zonas con \"requiere pago\" marcadas, el formulario público está oculto y los reportes son rechazados."}
          </div>
        </div>
        <button
          onClick={() => toggleModulo.mutate(!moduloActivo)}
          disabled={toggleModulo.isPending || modulo.isLoading}
          className={`relative inline-flex h-7 w-12 items-center rounded-full transition ${
            moduloActivo ? "bg-emerald-500" : "bg-ink-300"
          } disabled:opacity-50`}
          title={moduloActivo ? "Desactivar módulo" : "Activar módulo"}
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
            moduloActivo ? "translate-x-6" : "translate-x-1"
          }`} />
        </button>
      </div>

      {/* Stats */}
      {resumen.data && (
        <div className="grid grid-cols-2 tab:grid-cols-4 gap-3">
          <Stat label="Total" value={resumen.data.total} color="text-ink-800" emoji="📋" />
          <Stat label="Pendientes" value={resumen.data.pendientes} color="text-amber-700" emoji="⏳" highlight />
          <Stat label="Aprobados" value={resumen.data.verificados} color="text-emerald-700" emoji="✅" />
          <Stat label="Cobrado total" value={fmtUsd(resumen.data.totalCobradoUsd)} color="text-emerald-700" emoji="💵" big />
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-ink-200 overflow-x-auto">
        {[
          { key: "pendiente",  label: "Pendientes" },
          { key: "verificado", label: "Aprobados" },
          { key: "rechazado",  label: "Rechazados" },
          { key: "todos",      label: "Todos" },
        ].map((t) => (
          <button key={t.key} onClick={() => setTab(t.key as any)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${
              tab === t.key ? "border-emerald-500 text-emerald-700" : "border-transparent text-ink-500 hover:text-ink-700"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {lista.isLoading ? (
        <div className="text-center py-12 text-ink-400">
          <Loader2 className="size-6 animate-spin mx-auto mb-2" /> Cargando…
        </div>
      ) : items.length === 0 ? (
        <div className="card p-12 text-center">
          <p className="text-5xl mb-2">📭</p>
          <p className="font-medium text-ink-800">Sin pagos {tab !== "todos" ? tab + "s" : ""}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((p) => {
            const est = ESTADO_LABEL[p.estado] ?? ESTADO_LABEL.pendiente;
            const Icon = est.icono;
            return (
              <article key={p.id} className="card overflow-hidden">
                {/* Header con estado */}
                <div className="px-4 py-2.5 border-b border-ink-100 flex items-center gap-2 bg-ink-50/40">
                  <Icon className="size-4 text-ink-500" />
                  <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${est.color}`}>
                    {est.txt}
                  </span>
                  {p.origen === "telefono" ? (
                    <span className="text-xs px-2 py-0.5 rounded-full border font-semibold bg-blue-100 text-blue-700 border-blue-200 inline-flex items-center gap-1">
                      <PhoneCall className="size-3" /> Teléfono
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full border font-semibold bg-violet-100 text-violet-700 border-violet-200 inline-flex items-center gap-1">
                      <Smartphone className="size-3" /> Autoservicio
                    </span>
                  )}
                  <span className="text-xs text-ink-500 ml-auto">
                    reportado: {fmtFecha(p.createdAt)}
                  </span>
                </div>

                <div className="p-4 grid grid-cols-1 tab:grid-cols-3 gap-4">
                  {/* Datos del pago */}
                  <div className="tab:col-span-2 space-y-2">
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-ink-400 font-semibold">Pagador</p>
                      <p className="font-semibold text-ink-800">{p.nombrePagador}</p>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-ink-600">
                        {p.cedula && <span><Hash className="inline size-3" /> {p.cedula}</span>}
                        {p.telefono && (
                          <a href={`tel:${p.telefono}`} className="hover:text-ink-800">
                            <Phone className="inline size-3" /> {p.telefono}
                          </a>
                        )}
                        {p.email && (
                          <a href={`mailto:${p.email}`} className="hover:text-ink-800 truncate max-w-[200px]">
                            <Mail className="inline size-3" /> {p.email}
                          </a>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div>
                        <p className="text-[10px] uppercase tracking-wide text-ink-400 font-semibold">Monto</p>
                        <p className="font-bold text-emerald-700 text-lg">{p.moneda && p.moneda !== 'USD' ? `${p.moneda} ${Number(p.montoUsd).toFixed(2)}` : fmtUsd(p.montoUsd)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wide text-ink-400 font-semibold">Método</p>
                        <p className="text-sm text-ink-700">{p.metodo ?? "—"}</p>
                      </div>
                      {p.banco && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wide text-ink-400 font-semibold">Banco</p>
                          <p className="text-sm text-ink-700">{p.banco}</p>
                        </div>
                      )}
                      {p.numeroReferencia && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wide text-ink-400 font-semibold">Referencia</p>
                          <p className="text-sm text-ink-700 font-mono">{p.numeroReferencia}</p>
                        </div>
                      )}
                      {p.fechaPago && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wide text-ink-400 font-semibold">Fecha pago</p>
                          <p className="text-sm text-ink-700">{p.fechaPago?.slice(0, 10)}</p>
                        </div>
                      )}
                    </div>

                    {p.notasCliente && (
                      <div className="mt-2 bg-amber-50/50 border border-amber-100 rounded-lg p-2">
                        <p className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold">Notas</p>
                        <p className="text-xs text-ink-700">{p.notasCliente}</p>
                      </div>
                    )}

                    {p.motivoRechazo && (
                      <div className="mt-2 bg-rose-50 border border-rose-200 rounded-lg p-2">
                        <p className="text-[10px] uppercase tracking-wide text-rose-700 font-semibold">Motivo rechazo</p>
                        <p className="text-xs text-rose-800">{p.motivoRechazo}</p>
                      </div>
                    )}

                    {p.comprobanteUrl && (
                      <div className="mt-2">
                        <p className="text-[10px] uppercase tracking-wide text-ink-400 font-semibold mb-1">Comprobante</p>
                        {p.comprobanteUrl.match(/\.(jpg|jpeg|png|webp|gif)$/i) ? (
                          <a href={p.comprobanteUrl} target="_blank" rel="noopener" className="block">
                            <img src={p.comprobanteUrl} alt="Comprobante"
                              className="max-h-40 rounded-lg border border-ink-200 object-contain hover:opacity-80 transition" />
                          </a>
                        ) : (
                          <a href={p.comprobanteUrl} target="_blank" rel="noopener"
                            className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline">
                            <FileText className="size-3" /> Ver comprobante
                            <ExternalLink className="size-3" />
                          </a>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Datos de la reserva */}
                  <div className="border-t tab:border-t-0 tab:border-l border-ink-100 pt-3 tab:pt-0 tab:pl-4">
                    <p className="text-[10px] uppercase tracking-wide text-ink-400 font-semibold mb-1">Reserva</p>
                    <Link to={`/reservas/${p.reservaId}`}
                      className="font-semibold text-indigo-700 hover:underline inline-flex items-center gap-1">
                      #{p.reservaId} <ExternalLink className="size-3" />
                    </Link>
                    <p className="text-sm text-ink-800 mt-1">{p.nombreReserva}</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-ink-600">
                      <span><Calendar className="inline size-3" /> {p.fechaReserva?.slice(0,10)}</span>
                      <span><Clock className="inline size-3" /> {p.horaReserva?.slice(0,5)}</span>
                      <span><Users className="inline size-3" /> {p.numPersonas}</span>
                    </div>
                    <p className="text-[11px] text-ink-500 mt-2">
                      Estado: <span className="font-semibold">{p.estadoReserva}</span>
                    </p>
                  </div>
                </div>

                {/* Acciones - solo si pendiente */}
                {p.estado === "pendiente" && (
                  <div className="px-4 py-3 bg-ink-50 border-t border-ink-100">
                    {rechazandoId === p.id ? (
                      <div className="space-y-2">
                        <textarea value={motivoRechazo}
                          onChange={(e) => setMotivoRechazo(e.target.value)}
                          rows={2} placeholder="Motivo del rechazo (el cliente lo verá)…"
                          className="w-full text-sm border border-ink-300 rounded-lg px-3 py-2" />
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => { setRechazandoId(null); setMotivoRechazo(""); }}
                            className="px-3 py-1.5 text-sm rounded-lg border border-ink-300 hover:bg-ink-100">
                            Cancelar
                          </button>
                          <button
                            disabled={!motivoRechazo.trim() || rechazar.isPending}
                            onClick={() => rechazar.mutate({ id: p.id, motivo: motivoRechazo })}
                            className="px-3 py-1.5 text-sm rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50">
                            Confirmar rechazo
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => setRechazandoId(p.id)}
                          className="px-4 py-2 text-sm rounded-lg border border-rose-300 text-rose-700 hover:bg-rose-50">
                          ✗ Rechazar
                        </button>
                        <button onClick={() => aprobar.mutate(p.id)} disabled={aprobar.isPending}
                          className="px-4 py-2 text-sm rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1">
                          {aprobar.isPending && <Loader2 className="size-4 animate-spin" />}
                          <CheckCircle2 className="size-4" /> Aprobar y confirmar reserva
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      {/* ── Modal: Registrar pago manual ── */}
      {modalAbierto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => { setModalAbierto(false); setReservaSel(null); }}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-ink-200 px-5 py-4 rounded-t-2xl flex items-center justify-between z-10">
              <h2 className="font-display text-lg font-bold text-ink-900 flex items-center gap-2">
                <Banknote className="size-5 text-emerald-600" />
                {reservaSel ? `Pago — Reserva #${reservaSel.id}` : "Registrar pago manual"}
              </h2>
              <button onClick={() => { setModalAbierto(false); setReservaSel(null); }}
                className="p-1.5 rounded-lg hover:bg-ink-100 text-ink-500">
                <X className="size-5" />
              </button>
            </div>

            <div className="p-5">
              {!reservaSel ? (
                <>
                  <p className="text-sm text-ink-600 mb-3">
                    Seleccioná la reserva para registrar el pago:
                  </p>
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-ink-400" />
                    <input
                      autoFocus
                      value={busqueda}
                      onChange={(e) => setBusqueda(e.target.value)}
                      placeholder="Buscar por #, nombre, teléfono, cédula o zona…"
                      className="w-full pl-9 pr-3 py-2.5 border-2 border-ink-200 rounded-xl text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </div>
                  {pendientesPago.isLoading ? (
                    <div className="text-center py-8 text-ink-400">
                      <Loader2 className="size-5 animate-spin mx-auto mb-2" /> Cargando reservas…
                    </div>
                  ) : reservasFiltradas.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-ink-500 text-sm">No hay reservas pendientes de pago</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                      {reservasFiltradas.map((r) => (
                        <button key={r.id} onClick={() => setReservaSel(r)}
                          className="w-full text-left p-3 rounded-xl border-2 border-ink-200 hover:border-emerald-400 hover:bg-emerald-50/30 transition">
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-ink-800">#{r.id} — {r.nombreCliente}</span>
                            {r.montoReservaUsd && (
                              <span className="text-sm font-semibold text-emerald-700">
                                USD {Number(r.montoReservaUsd).toFixed(2)}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-ink-500">
                            <span><Calendar className="inline size-3" /> {r.fechaReserva?.slice(0, 10)}</span>
                            <span><Clock className="inline size-3" /> {r.horaReserva?.slice(0, 5)}</span>
                            <span><Users className="inline size-3" /> {r.numPersonas}p</span>
                            <span>{r.zonaNombre}</span>
                            {r.telefono && <span><Phone className="inline size-3" /> {r.telefono}</span>}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <button onClick={() => setReservaSel(null)}
                    className="text-xs text-indigo-600 hover:underline mb-3 inline-flex items-center gap-1">
                    ← Cambiar reserva
                  </button>
                  <div className="bg-ink-50 rounded-xl p-3 mb-4 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-ink-800">{reservaSel.nombreCliente}</span>
                      <span className="text-xs text-ink-500">{reservaSel.zonaNombre}</span>
                    </div>
                    <div className="flex gap-3 mt-1 text-xs text-ink-500">
                      <span>{reservaSel.fechaReserva?.slice(0, 10)}</span>
                      <span>{reservaSel.horaReserva?.slice(0, 5)}</span>
                      <span>{reservaSel.numPersonas} personas</span>
                    </div>
                  </div>
                  <FormPagoReserva
                    reservaId={reservaSel.id}
                    montoSugerido={reservaSel.montoReservaUsd ? Number(reservaSel.montoReservaUsd) : null}
                    origen="telefono"
                    defaults={{
                      nombre: reservaSel.nombreCliente,
                      telefono: reservaSel.telefono,
                      email: reservaSel.email,
                      cif: reservaSel.cif,
                    }}
                    onReportado={() => {
                      setModalAbierto(false);
                      setReservaSel(null);
                      qc.invalidateQueries({ queryKey: ["pagos-reservas"] });
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color, emoji, highlight, big }: {
  label: string; value: any; color?: string; emoji: string; highlight?: boolean; big?: boolean;
}) {
  return (
    <div className={`rounded-xl border p-3 text-center ${highlight ? "bg-amber-50 border-amber-200" : "bg-white border-ink-200"}`}>
      <div className="text-xl">{emoji}</div>
      <div className={`font-bold ${big ? "text-lg" : "text-2xl"} ${color}`}>{value}</div>
      <div className="text-[11px] text-ink-500 uppercase tracking-wide">{label}</div>
    </div>
  );
}
